import React, { useContext } from "react";
import { Link, Redirect, Route, Switch } from "react-router-dom";
import { useSelector } from "react-redux";
import useDocTitle from "../helpers/hooks/useDocTitle";
import LegacyRouteRedirect from "../helpers/LegacyRouteRedirect";
import { stringRandom } from "../helpers/stringRandom";
import Faq from "../components/landing/pages/Faq";
import CurrencyConfig from "../components/currency/CurrencyConfig";
import { CurrencyContext } from "../context/Context";
import { Alert, Col, Row } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCoins } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import Dashboard from "../components/dashboard/Dashboard";
import MyProfile from "../components/properties/profile/MyProfile";
import Feed from "./../components/feed/Feed";
import CondominiumLayout from "../layouts/CondominiumLayout";
import BanksLayout from "../layouts/BanksLayout";
import FinanceLayout from "../layouts/FinanceLayout";
import Config from "../components/config/Config";
import ProvidersLayout from "../layouts/ProvidersLayout";
import PropertiesLayout from "../layouts/PropertiesLayout";
import { ProtectedRoute } from "../helpers/ProtectedRoute";
import MyLayout from "../layouts/MyLayout";
import SupportLayout from "../layouts/SupportLayout";
import ComplainLayout from "../layouts/ComplainLayout";
import ParkingLayout from "../layouts/ParkingLayout";
import SurveysLayout from "../layouts/SurveysLayout";
import RewardsLayout from "../layouts/RewardsLayout";
import MarketplaceLayout from "../layouts/MarketplaceLayout";
import { REWARDS_ENABLED } from "../helpers/rewardsEnabled";
import { MARKETPLACE_ENABLED } from "../helpers/marketplaceEnabled";
import SecurityLayout from "../layouts/SecurityLayout";
import VisitorsLayout from "../layouts/VisitorsLayout";
import AreasLayout from "../layouts/AreasLayout";
import Notifications from "../components/notifications/Notifications";
import ReserveLayout from "../layouts/ReserveLayout";
import Release from "../components/release/Release";
import PeriodLayout from "../layouts/PeriodLayout";
import ExchangeConfig from "../components/exchange/ExchangeConfig";
import AccountingLayout from "../layouts/AccountingLayout";
import AliquotsLayout from "../layouts/AliquotsLayout";
import Breadcrumbs from "../components/common/Breadcrumbs";

const DashboardRoutes = () => {
  const { selectedCondominium } = useSelector((state) => state.condominium);
  const { currency: curr, loading, baseCurrency } = useContext(CurrencyContext);
  const { currency } = useSelector((state) => state.auth);
  const { role, token, hasMultipleProperties } = useSelector(
    (state) => state.auth.user
  );
  const currentExchangeRate = Number(curr?.value ?? 0);
  const { selectedProperty } = useSelector((state) => state.property);

  document.title = useDocTitle();

  // HO-HU-37: mientras una cuenta con varias propiedades no eligio
  // ninguna, ninguna ruta del dashboard aparte de /dashboard/app (donde
  // esta el selector) es accesible -- ocultar el menu no alcanza si
  // alguien escribe la URL directo. Con 0-1 propiedades esto nunca aplica.
  //
  // "Perfil y cuenta" es la excepcion: no depende de ninguna propiedad
  // puntual (nombre, contraseña, contacto de la cuenta en si), asi que no
  // tiene sentido bloquearlo hasta elegir una -- antes rebotaba a
  // /dashboard/app igual que las rutas si dependientes de una propiedad
  // (finanzas, parqueos, etc.), dando la impresion de que el link "no hacia
  // nada".
  const mustSelectProperty =
    role === "property" && hasMultipleProperties && !selectedProperty;

  if (mustSelectProperty) {
    return (
      <>
        <Breadcrumbs />
        <Switch>
          <Route path={`/dashboard/app`} exact key={`/app`}>
            <Dashboard />
          </Route>
          <Route path={`/dashboard/my/profile`} exact key={`/my-profile`}>
            <MyProfile />
          </Route>
          <Redirect to={`/dashboard/app`} />
        </Switch>
      </>
    );
  }

  return (
    <>
      {!!currency && loading ? (
        <Alert color="info" className="shadow-sm">
          Obteniendo información de la tasa de cambio...
        </Alert>
      ) : null}
      {!!currency && !loading ? (
        <Alert color="info" className="shadow-sm">
          <Row>
            <Col
              xs="auto"
              className="d-flex align-items-center justify-content-center"
            >
              <FontAwesomeIcon icon={faCoins} />
            </Col>
            <Col>
              <Row>
                <Col>
                  {curr ? (
                    <span>
                      <b>{`${curr?.currency?.iso} ${currentExchangeRate.toFixed(
                        2
                      )} = ${baseCurrency?.symbol || "$"} 1.00`}</b>{" "}
                      al{" "}
                      {curr.date
                        ? moment(curr.date).format("DD/MM/YYYY")
                        : moment(curr.created_at).format(
                            "DD/MM/YYYY [a las] h:mm A"
                          )}
                    </span>
                  ) : (
                    "Esta moneda aún no tiene tasa de cambio"
                  )}
                </Col>
              </Row>
              {role !== "property" && (
                <Row>
                  <Col>
                    <span className="fs--1">
                      Puedes cambiar la moneda por defecto, ve a la{" "}
                      <Link to="/dashboard/currency">
                        configuración de moneda
                      </Link>
                      .
                    </span>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        </Alert>
      ) : null}
      {/* HO-TASK-250: las migas van debajo del banner de la tasa para que el
      banner quede pegado a la barra superior y la ruta acompañe al contenido. */}
      <Breadcrumbs />
      <Switch>
        {!!selectedCondominium && (
          <Route path={`/dashboard/feed`} key={"feed"}>
            <Feed />
          </Route>
        )}
        {!!selectedCondominium && (
          <Route path={`/dashboard/release`} key={"release"}>
            <Release />
          </Route>
        )}
        <Route path={`/dashboard/areas`} key={`/areas`}>
          <AreasLayout />
        </Route>
        <Route path={`/dashboard/app`} exact key={`/app`}>
          <Dashboard />
        </Route>
        <ProtectedRoute
          path={`/dashboard/condominiums`}
          rols={["manager"]}
          key={stringRandom(3)}
        >
          <CondominiumLayout />
        </ProtectedRoute>
        {/*   <Route path={`/dashboard/exchange`} exact component={ExchangeConfig} /> */}

        <Route path={`/dashboard/exchange`} key={"exchange"}>
          <ExchangeConfig />
        </Route>
        <Route path={`/dashboard/banks`} key={"banks"}>
          <BanksLayout />
        </Route>
        <Route path={`/dashboard/periods`} key={"periods"}>
          <PeriodLayout />
        </Route>
        <Route path={`/dashboard/finance`} key={"finance"}>
          <FinanceLayout />
        </Route>
        <Route path={`/dashboard/accounting`} key={"accounting"}>
          <AccountingLayout />
        </Route>
        <Route path={`/dashboard/aliquots`} key={"aliquots"}>
          <AliquotsLayout />
        </Route>
        <Route path={`/dashboard/funds`} key={"funds"}>
          <ReserveLayout />
        </Route>
        <ProtectedRoute
          path={`/dashboard/currency`}
          rols={["manager", "property"]}
          key={"currency"}
        >
          <CurrencyConfig />
        </ProtectedRoute>
        <Route path={`/dashboard/properties`} key={"properties"}>
          <PropertiesLayout />
        </Route>
        <Route path={`/dashboard/providers`} key={"providers"}>
          <ProvidersLayout />
        </Route>
        <Route path={`/dashboard/notifications`} key={"notifications"}>
          <Notifications />
        </Route>
        <Route path={`/dashboard/faqs`} key={"faqs"}>
          <Faq />
        </Route>
        <Route path={`/dashboard/security`} key={"security"}>
          <SecurityLayout />
        </Route>
        <Route path={`/dashboard/visitors`} key={"visitors"}>
          <VisitorsLayout />
        </Route>
        <ProtectedRoute
          path={`/dashboard/config`}
          rols={["manager"]}
          key={"config"}
        >
          <Config />
        </ProtectedRoute>
        <Route path={`/dashboard/my`} key={"my"}>
          <MyLayout />
        </Route>
        <Route path={`/dashboard/support`} key={"support"}>
          <SupportLayout />
        </Route>
        <Route path={`/dashboard/complaints`} key={"complaints"}>
          <ComplainLayout />
        </Route>
        <Route path={`/dashboard/surveys`} key={"surveys"}>
          <SurveysLayout />
        </Route>
        {REWARDS_ENABLED && (
          <Route path={`/dashboard/rewards`} key={"rewards"}>
            <RewardsLayout />
          </Route>
        )}
        {MARKETPLACE_ENABLED && (
          <Route path={`/dashboard/marketplace`} key={"marketplace"}>
            <MarketplaceLayout />
          </Route>
        )}
        <Route path={`/dashboard/parking`} key={"parking"}>
          <ParkingLayout />
        </Route>
        {/* Reescribe las URLs viejas (camelCase, y /dashboard/reserve antes de
        pasar a /dashboard/funds) antes de caer al 404. */}
        <LegacyRouteRedirect />
      </Switch>
    </>
  );
};

export default DashboardRoutes;
